import { chatDrawerActions } from 'ducks/chatDrawer'
import { isActionWhitelisted, requiresConfirmation } from 'layout/widgets/adaChat/actionPermissions'
import {
  isInDesignTab,
  validateActionRequirementsOrThrow,
} from 'layout/widgets/adaChat/utils/validateActionRequirements'
import { requestAutoStringing } from 'reducer/designer/autoString'
import { LiveFunctionCall } from '../GeminiLiveAPI/lib/multimodal-live-types'

interface RunAutoStringArgs {
  confirmed?: boolean // Optional flag to indicate user has confirmed
}

export const TOOL_NAME = 'run_auto_string'

export async function RunAutoStringToolCall(functionCall: LiveFunctionCall, history: any, orgId: number | undefined) {
  const args = functionCall.args as RunAutoStringArgs

  console.log('RunAutoStringToolCall', args)

  const pathname = window.location.pathname
  validateActionRequirementsOrThrow('runAutoString', pathname)

  // Extract project ID from pathname
  const { projectId } = isInDesignTab(pathname)

  if (!projectId) {
    throw new Error('Project ID is required to run auto string')
  }

  // Check if this action requires confirmation
  const isWhitelisted = isActionWhitelisted('runAutoString')

  if (requiresConfirmation('runAutoString') && !isWhitelisted) {
    // Check if confirmation was explicitly provided in args
    if (!args.confirmed) {
      const errorMessage =
        'This action requires confirmation. Please ask the user: "Would you like me to run auto string? This will replace current stringing." and wait for their confirmation before proceeding.'
      console.error('[Voice] Auto string requires confirmation but confirmed flag not set:', errorMessage)
      throw new Error(errorMessage)
    }
  }

  // Get the selected system
  const system = window.editor?.selectedSystem
  if (!system) {
    throw new Error('No system selected. Please select a system in the design.')
  }

  // Validate system requirements
  if (system.inverters().length === 0) {
    throw new Error('Auto String requires at least one inverter to be added to the system.')
  }

  if (system.moduleQuantity() === 0) {
    throw new Error('Auto String requires panels to be placed in the system.')
  }

  if (system.hasMultipleModuleTypes()) {
    throw new Error(
      'Auto Stringing is not available as multiple panels are present on system. Please manually string panel groups.'
    )
  }

  const inverters = system.inverters()
  if (inverters.length > 1 && inverters.some((inverter) => inverter.code !== inverters[0].code)) {
    throw new Error('Auto String is only available when a single inverter type is used.')
  }

  console.log('[Voice] Starting runAutoString for system:', system.uuid)

  // Create a message with toolExecutionState so the AutoStringProgressItem shows
  if (window.reduxStore && projectId) {
    const state = window.reduxStore.getState()
    const messages = state?.chatDrawer?.messages || []
    const lastMessageIndex = messages.length > 0 ? messages.length - 1 : -1

    // If there's a previous message, attach toolExecutionState to it
    // Otherwise create a new message
    if (lastMessageIndex >= 0) {
      window.reduxStore.dispatch(
        chatDrawerActions.updateToolExecutionState(lastMessageIndex, {
          toolType: 'autoString',
          status: 'running',
          projectId: projectId,
          isAutoAccepted: isWhitelisted,
        })
      )
    } else {
      // Fallback: create a new message if there are no messages
      window.reduxStore.dispatch(chatDrawerActions.addMessage('Running auto string...', 'receive', 'voice'))
      // Then add toolExecutionState to the newly created message
      const newState = window.reduxStore.getState()
      const newMessages = newState?.chatDrawer?.messages || []
      const newLastMessageIndex = newMessages.length > 0 ? newMessages.length - 1 : -1
      if (newLastMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(newLastMessageIndex, {
            toolType: 'autoString',
            status: 'running',
            projectId: projectId,
            isAutoAccepted: isWhitelisted,
          })
        )
      }
    }
  }

  // Dispatch the auto-stringing request
  const firstInverter = inverters[0]
  if (!window.reduxStore) {
    throw new Error('Redux store not available')
  }

  window.reduxStore.dispatch(requestAutoStringing(system.uuid, 'best', undefined, firstInverter.inverter_id, true))

  // Subscribe to Redux store to detect when auto-stringing completes
  // The saga will set autoStringRunning to undefined when done
  let isCompleted = false

  const unsubscribe = window.reduxStore.subscribe(() => {
    if (isCompleted) return

    const state = window.reduxStore.getState()
    const autoStringRunning = state?.designer?.autoString?.running

    // When autoStringRunning becomes undefined, auto-stringing has completed
    if (!autoStringRunning) {
      isCompleted = true
      unsubscribe()

      const messages = state?.chatDrawer?.messages || []

      // Find the last receive message with autoString toolExecutionState
      let targetMessageIndex = -1
      for (let i = messages.length - 1; i >= 0; i--) {
        if (
          messages[i].direction === 'receive' &&
          messages[i].toolExecutionState &&
          messages[i].toolExecutionState.toolType === 'autoString'
        ) {
          targetMessageIndex = i
          break
        }
      }

      if (targetMessageIndex >= 0) {
        window.reduxStore.dispatch(
          chatDrawerActions.updateToolExecutionState(targetMessageIndex, {
            toolType: 'autoString',
            status: 'completed',
            description: 'Auto stringing complete',
            projectId: projectId,
          })
        )
        console.log('[Voice] Auto stringing completed')
      }
    }
  })

  // Return immediately - tool call state will be cleared by VoiceProvider
  // The auto-stringing process continues in the background
  return {
    success: true,
    message: 'Auto stringing started. This will replace current stringing.',
  }
}
